import { useMemo, useEffect, useState } from 'react';
import { useFeatureFlagCore, type FeatureFlagAdapter } from '@pantry/shared-react/hooks';
import { posthog, isPostHogAvailable } from '@/lib/posthog';
import type { FeatureFlag } from '@pantry/analytics';

/**
 * Hook to check if a feature flag is enabled.
 * Automatically updates when flags are refreshed.
 *
 * Example:
 *   const isReceiptScanEnabled = useFeatureFlag(FeatureFlags.RECEIPT_SCANNING);
 */
export function useFeatureFlag(flag: FeatureFlag): boolean | null {
  const adapter = useMemo<FeatureFlagAdapter>(
    () => ({
      getFlag: (f) => (isPostHogAvailable() ? (posthog.isFeatureEnabled(f) ?? null) : null),
      subscribe: (cb) => (isPostHogAvailable() ? posthog.onFeatureFlags(cb) : undefined),
    }),
    []
  );
  return useFeatureFlagCore(flag, adapter);
}

/**
 * Hook to check multiple feature flags at once.
 * Returns an object with flag names as keys and boolean values.
 *
 * Example:
 *   const flags = useFeatureFlags([FeatureFlags.RECEIPT_SCANNING, FeatureFlags.AI_SUGGESTIONS]);
 *   if (flags[FeatureFlags.RECEIPT_SCANNING]) { ... }
 */
export function useFeatureFlags<T extends FeatureFlag>(flags: T[]): Record<T, boolean | null> {
  // Memoize flags array to prevent infinite re-renders
  const flagKey = flags.join(',');
  // flagKey is a stable string derived from flags — use it instead of the array reference
  const memoizedFlags = useMemo(() => flags, [flagKey]);

  const [values, setValues] = useState<Record<T, boolean | null>>(() =>
    memoizedFlags.reduce((acc, flag) => ({ ...acc, [flag]: null }), {} as Record<T, boolean | null>)
  );

  useEffect(() => {
    if (!isPostHogAvailable()) return;

    const updateFlags = () => {
      setValues(
        memoizedFlags.reduce(
          (acc, flag) => ({
            ...acc,
            [flag]: posthog.isFeatureEnabled(flag) ?? null,
          }),
          {} as Record<T, boolean | null>
        )
      );
    };

    updateFlags();
    const unsubscribe = posthog.onFeatureFlags(updateFlags);

    return unsubscribe;
  }, [memoizedFlags]);

  return values;
}
